import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  Popper,
  PopperProps,
} from '@mui/material';
import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import React, { FC, useState, type JSX } from 'react';
import BasicField from '../basicField/BasicField';
import { LabelStyleVariant } from '../fieldLabel/FieldLabel';
import { InputStyleVariant } from '../inputWrapper/InputWrapper';
import MiniLoader from '../miniLoader/MiniLoader';
import './TypeToSearchField.sass';

export type TypeToSearchRenderOptionProps =
  React.HTMLAttributes<HTMLLIElement> & { key: any };
type Props<T> = {
  options?: T[];
  isLoadingOptions?: boolean;
  disableFilteringInComponent?: boolean;
  disableShowOptions?: boolean;
  // use if multiple is false
  onSelectOption?: (option?: T) => void;
  // use if multiple is true
  onSelectOptions?: (options?: T[]) => void;
  onSearchTermChange?: (searchTerm: string) => void;
  onSearchTermChangeTimeout?: () => void;
  onEnter?: () => void;
  renderOption?: (
    option: T,
    props: TypeToSearchRenderOptionProps
  ) => React.ReactNode;
  getOptionLabel?: (option: T) => string;
  styleVariant?: InputStyleVariant;
  labelStyleVariant?: LabelStyleVariant;
  changeTimeoutMs?: number;
  placeholder?: string;
  label?: string;
  description?: string;
  noOptionsText?: React.ReactNode;
  value?: string;
  onOpen?: () => void;
  isRequired?: boolean;
  iconAfter?: TIconName | JSX.Element;
  iconBefore?: TIconName | JSX.Element;
  autofocus?: boolean;
  onBlur?: () => void;
  additionalInputProps?: any;
  getOptionDisabled?: (option: T) => boolean;
  autoHighlight?: boolean;
  disableCloseOnSelect?: boolean;
  blockInputChangeReasons?: AutocompleteInputChangeReason[];
  openOnFocus?: boolean;
  multiple?: boolean;
  defaultValue?: any;
  isDisabled?: boolean;
  dynamicPopperWidth?: boolean;
};

const TypeToSearchField = <T extends object>({
  options,
  isLoadingOptions = false,
  disableFilteringInComponent = false,
  disableShowOptions = false,
  onSelectOption,
  onSelectOptions,
  onSearchTermChangeTimeout,
  onSearchTermChange,
  renderOption,
  getOptionLabel,
  onEnter,
  styleVariant,
  labelStyleVariant,
  changeTimeoutMs = 1000,
  placeholder,
  label,
  description,
  noOptionsText,
  value,
  multiple,
  onOpen,
  isRequired = false,
  iconAfter,
  iconBefore,
  autofocus,
  onBlur,
  additionalInputProps = {},
  getOptionDisabled,
  disableCloseOnSelect = false,
  blockInputChangeReasons = [],
  openOnFocus,
  autoHighlight,
  defaultValue,
  isDisabled,
  dynamicPopperWidth = false,
}: Props<T>): JSX.Element => {
  const [changeTimeout, setChangeTimeout] = useState<any>();
  const [isOpen, setIsOpen] = useState(false);

  const hasLabel = (option: any): option is { label: string } => {
    return typeof option === 'object' && option !== null && 'label' in option;
  };

  return (
    <div className="type-to-search-field">
      <Autocomplete
        multiple={multiple}
        disableCloseOnSelect={disableCloseOnSelect}
        getOptionDisabled={getOptionDisabled}
        autoHighlight={autoHighlight}
        openOnFocus={openOnFocus}
        disabled={isDisabled}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && onEnter) {
            // Prevent's default 'Enter' behavior.
            event.defaultMuiPrevented = true;
            onEnter();
          }
        }}
        id="combo-box-demo"
        inputValue={value}
        open={isOpen && !disableShowOptions && !isDisabled}
        onOpen={() => {
          setIsOpen(true);
          onOpen?.();
        }}
        onClose={() => setIsOpen(false)}
        options={options || []}
        classes={{ paper: 'type-to-search-field-paper' }}
        getOptionLabel={(option: any) => {
          return getOptionLabel
            ? getOptionLabel(option as T)
            : option?.label || '';
        }}
        renderOption={(props, option) => {
          if (renderOption) {
            return renderOption(option, props);
          }
          return (
            <li {...props}>
              {hasLabel(option) ? option.label : JSON.stringify(option)}
            </li>
          );
        }}
        filterOptions={
          disableFilteringInComponent ? (options: any) => options : undefined
        }
        onChange={(event: any, option: T | string | (string | T)[] | null) => {
          if (!option) {
            onSelectOption?.();
          } else {
            if (multiple) {
              onSelectOptions?.(option as T[]);
            } else {
              onSelectOption?.(option as T);
            }
          }
        }}
        noOptionsText={noOptionsText}
        onInputChange={(
          event: any,
          value: string,
          reason: AutocompleteInputChangeReason
        ) => {
          if (!event) return;
          if (!!onEnter && event?.key === 'Enter') return;
          if (blockInputChangeReasons.includes(reason)) {
            return;
          }
          onSearchTermChange?.(value);
          if (changeTimeout) {
            clearTimeout(changeTimeout);
          }
          setChangeTimeout(
            setTimeout(() => {
              onSearchTermChangeTimeout?.();
            }, changeTimeoutMs)
          );
        }}
        defaultValue={defaultValue}
        freeSolo={!noOptionsText}
        onBlur={onBlur}
        renderInput={(params: AutocompleteRenderInputParams) => {
          return (
            <div ref={params.InputProps.ref}>
              <BasicField
                label={label}
                labelStyleVariant={labelStyleVariant}
                styleVariant={styleVariant}
                name="searchTerm"
                isDisabled={params.disabled}
                isRequired={isRequired}
                description={description}
                additionalInputProps={{
                  ...params.inputProps,
                  autoComplete: 'off',
                  autoFocus: autofocus,
                  ...additionalInputProps,
                }}
                placeholder={placeholder}
                iconAfter={
                  iconAfter === undefined ? (
                    isLoadingOptions ? (
                      <MiniLoader />
                    ) : (
                      'search-lg.3'
                    )
                  ) : (
                    iconAfter
                  )
                }
                iconBefore={iconBefore}
              />
            </div>
          );
        }}
        PopperComponent={dynamicPopperWidth ? FitContentPopper : undefined}
      />
    </div>
  );
};

const FitContentPopper: FC<PopperProps> = (props) => {
  return (
    <Popper
      {...props}
      style={{ width: 'fit-content', minWidth: props.style?.width }}
      placement="bottom-start"
    />
  );
};

export default TypeToSearchField;
